<script setup lang="ts">
import { nextTick, ref, useId, watch } from 'vue'

import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import FormInput from '@tix/ds/components/atoms/FormInput.vue'

export type DonationPreset = {
  id: string
  /** Display label, pre-formatted for the active currency (e.g. "$50", "£100"). */
  label: string
}

export type DonationCustomAmount = {
  id: string
  /** ISO 4217 currency code used to render the input's currency symbol. */
  currency: string
  min?: number
  max?: number
}

export type DonationAmountSelection = {
  ticketTypeId: string
  faceValue?: number
}

type Props = {
  heading?: string
  /** Render order matches array order; the widget does not sort. */
  presetAmounts: DonationPreset[]
  /** Pay-what-you-want field. Pass `null` to hide it. */
  customAmount?: DonationCustomAmount | null
  /**
   * The authoritative selection (cart-derived). The widget echoes local intent
   * optimistically on interaction, but reconciles to this whenever it changes —
   * e.g. after a server round-trip corrects or drops the cart. `null` = nothing
   * selected.
   */
  selection?: DonationAmountSelection | null
  /** Freeze presets + custom amount (e.g. while payment submit is in flight). */
  disabled?: boolean
  /** Red card border after Pay with no reserved amount. */
  invalid?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  heading: 'How would you like to donate?',
  customAmount: null,
  selection: null,
  disabled: false,
  invalid: false,
})

const emit = defineEmits<{
  selectPreset: [id: string]
  /** `amount: null` means the field is empty. */
  'update:customAmount': [amount: number | null]
}>()

const headingId = useId()
const selected = ref<string | null>(props.selection?.ticketTypeId ?? null)
const customAmountValue = ref<number | null>(
  props.selection?.ticketTypeId === props.customAmount?.id ? (props.selection?.faceValue ?? null) : null,
)
const customAmountInput = ref<{ validateAndReturnError: () => string | boolean | null } | null>(null)

// Reconcile local UI to the authoritative selection whenever it changes (e.g. a
// server round-trip corrected or dropped the cart). Only refill the custom field
// when the cart actually holds a custom amount — an empty/invalid custom entry
// clears the cart (selection === null) but the typed value should stay put.
watch(
  () => props.selection,
  (selection) => {
    selected.value = selection?.ticketTypeId ?? null
    if (selection && props.customAmount && selection.ticketTypeId === props.customAmount.id) {
      customAmountValue.value = selection.faceValue ?? null
    }
  },
)

const handlePresetClick = (preset: DonationPreset) => {
  if (props.disabled) return

  selected.value = preset.id
  customAmountValue.value = null
  nextTick(() => {
    // Clear any validation errors from the previous custom amount.
    customAmountInput.value?.validateAndReturnError()
  })
  emit('selectPreset', preset.id)
}

const handleCustomAmountUpdate = (value: string) => {
  if (props.disabled || !props.customAmount) return

  selected.value = props.customAmount.id
  // Empty field emits ''; Number('') is 0, so treat empty as null explicitly.
  const numeric = value === '' ? null : Number(value)
  customAmountValue.value = Number.isFinite(numeric) ? numeric : null
  customAmountInput.value?.validateAndReturnError()
  // TODO Consider debouncing this to avoid excessive cart requests.
  emit('update:customAmount', customAmountValue.value)
}
</script>

<template>
  <section class="donation-widget" :class="{ 'is-invalid': invalid }">
    <header class="donation-widget-header">
      <h3 :id="headingId" class="donation-widget-heading">{{ heading }}</h3>
    </header>

    <div class="donation-widget-body">
      <div v-if="presetAmounts.length" :aria-labelledby="headingId" class="donation-widget-presets" role="radiogroup">
        <BaseButton
          v-for="preset in presetAmounts"
          :key="preset.id"
          :aria-checked="selected === preset.id"
          class="donation-widget-preset"
          :disabled="disabled"
          :hierarchy="selected === preset.id ? 'primary' : 'secondary'"
          intent="neutral"
          role="radio"
          type="button"
          @click="handlePresetClick(preset)"
        >
          {{ preset.label }}
        </BaseButton>
      </div>

      <FormInput
        v-if="customAmount"
        :id="customAmount.id"
        ref="customAmountInput"
        :currency="customAmount.currency"
        :disabled="disabled"
        :hideRequiredLabel="true"
        label="Other amount"
        :max="customAmount.max"
        :min="customAmount.min"
        :modelValue="customAmountValue ?? undefined"
        placeholder="Other amount"
        :showOptionalLabel="false"
        step="0.01"
        type="money"
        @update:modelValue="handleCustomAmountUpdate"
      />
    </div>
  </section>
</template>

<style scoped>
.donation-widget {
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: var(--elevation-0);
  border: var(--border-width) solid var(--action-card-border);
  border-radius: var(--card-border-radius);
  box-shadow: var(--shadow-lg);
  overflow: hidden;

  &.is-invalid {
    border-color: var(--error-border-color);
  }

  .donation-widget-header {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-1);
    background-color: var(--elevation-0);
    border-bottom: var(--border-width) solid var(--action-card-border);

    .donation-widget-heading {
      flex: 1;
      text-align: center;
    }
  }

  .donation-widget-body {
    display: flex;
    flex-direction: column;
    gap: var(--space-1-5);
    padding: var(--space-1);
  }

  .donation-widget-presets {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-0-75);
  }

  .donation-widget-preset {
    width: 100%;
  }

  @media (--desktop-experience) {
    .donation-widget-header,
    .donation-widget-body {
      padding: var(--space-1-5);
    }
  }
}
</style>
